import { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { StatMetric } from '@/components/StatMetric';
import { TrendChart } from '@/components/TrendChart';
import { TrendIcon } from '@/components/Icons';
import { useAttendanceState } from '@/context/AttendanceContext';
import { useSessionState } from '@/context/SessionContext';
import { useHomeDashboard } from '@/hooks/useHomeDashboard';
import { formatCompactNumber, formatInrCurrency, getInitials } from '@/lib/format';
import { tintStyle } from '@/lib/style';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export function AnalysisPage() {
  const { isReady: sessionReady, isEnterprise } = useSessionState();
  const { sessions } = useAttendanceState();
  const { data: homeData, isLoading, isFromApi, error, refresh } = useHomeDashboard();

  const staffRanking = useMemo(
    () => [...homeData.staff_performance].sort((left, right) => right.revenue - left.revenue).slice(0, 8),
    [homeData.staff_performance]
  );

  const serviceMix = useMemo(() => homeData.service_mix.slice(0, 6), [homeData.service_mix]);

  const peakDay = useMemo(() => {
    const entry = Object.entries(homeData.calendar.booking_density).sort((left, right) => right[1] - left[1])[0];
    if (!entry) {
      return 'No bookings yet';
    }
    return `Day ${entry[0]} · ${entry[1]} booking${entry[1] === 1 ? '' : 's'}`;
  }, [homeData.calendar.booking_density]);

  const busyDayCount = useMemo(
    () => Object.values(homeData.calendar.booking_density).filter((count) => count > 0).length,
    [homeData.calendar.booking_density]
  );

  const activeShifts = useMemo(
    () => sessions.filter((session) => session.clockOutAt === null).length,
    [sessions]
  );

  const completedShiftsToday = useMemo(() => {
    const today = new Date();
    return sessions.filter((session) => {
      if (!session.clockOutAt) {
        return false;
      }
      const clockOut = new Date(session.clockOutAt);
      return (
        clockOut.getFullYear() === today.getFullYear() &&
        clockOut.getMonth() === today.getMonth() &&
        clockOut.getDate() === today.getDate()
      );
    }).length;
  }, [sessions]);

  const feedbackItems = useMemo(() => (homeData.feedback ?? []).slice(0, 5), [homeData.feedback]);
  const recentAppointments = useMemo(
    () => (homeData.today_appointments ?? []).slice(0, 5),
    [homeData.today_appointments]
  );

  const topClient = useMemo(() => {
    const clients = homeData.clients ?? [];
    if (clients.length === 0) {
      return null;
    }
    return [...clients].sort((left, right) => right.total_visits - left.total_visits)[0] ?? null;
  }, [homeData.clients]);

  if (!sessionReady || isLoading) {
    return <div className="section">Loading analysis...</div>;
  }

  if (!isEnterprise) {
    return <Navigate to="/attendance" replace />;
  }

  const metrics = homeData.metrics;
  const overview = homeData.overview;

  return (
    <div className="section page-grid analysis-page">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <TrendIcon size={14} />
            <strong>Analysis</strong>
            <span>{isFromApi ? 'Live API' : homeData.month_label}</span>
          </span>

          <h1 className="display-title">
            <span>Business pulse</span>
          </h1>

          <p className="copy">
            Revenue, bookings, staff output, and service mix in one admin view.
          </p>

          {error ? (
            <div className="notice error">
              <span>{error}</span>
              <button className="ghost-button" type="button" onClick={refresh}>
                Retry
              </button>
            </div>
          ) : null}
        </div>

        <div className="stats-grid">
          <StatMetric
            label="Bookings"
            value={formatCompactNumber(metrics.weekly_bookings)}
            hint="This week"
            highlight
          />
          <StatMetric
            label="Revenue"
            value={formatInrCurrency(metrics.weekly_revenue)}
            hint="Gross this week"
          />
          <StatMetric label="Retention" value={`${metrics.retention}%`} hint="Repeat clients" />
          <StatMetric label="Rating" value={metrics.rating.toFixed(1)} hint="Team average" />
        </div>
      </section>

      <section className="analysis-kpi-strip">
        <div className="analysis-kpi">
          <span>Avg ticket</span>
          <strong>{formatInrCurrency(metrics.average_ticket)}</strong>
        </div>
        <div className="analysis-kpi">
          <span>Team score</span>
          <strong>{metrics.team_score}</strong>
        </div>
        <div className="analysis-kpi">
          <span>Clients</span>
          <strong>{formatCompactNumber(overview?.total_clients ?? homeData.clients?.length ?? 0)}</strong>
        </div>
        <div className="analysis-kpi">
          <span>All-time revenue</span>
          <strong>{formatInrCurrency(overview?.total_revenue ?? metrics.weekly_revenue)}</strong>
        </div>
        <div className="analysis-kpi">
          <span>On shift</span>
          <strong>{activeShifts}</strong>
        </div>
        <div className="analysis-kpi">
          <span>Shifts done today</span>
          <strong>{completedShiftsToday}</strong>
        </div>
      </section>

      <section className="analysis-grid">
        <div className="chart-card analysis-chart-card">
          <div className="panel-title-row">
            <div>
              <div className="badge">Trend</div>
              <h2 className="panel-title">Weekly revenue & bookings</h2>
            </div>
            <div className="chart-legend" aria-hidden="true">
              <span className="chart-legend-item">
                <i className="chart-legend-dot accent" />
                Revenue
              </span>
              <span className="chart-legend-item">
                <i className="chart-legend-dot info" />
                Bookings
              </span>
            </div>
          </div>
          <TrendChart data={homeData.weekly_trend} />
        </div>

        <div className="panel">
          <div className="panel-title-row">
            <div>
              <h2 className="panel-title">Service mix</h2>
              <p className="panel-caption">{homeData.month_label}</p>
            </div>
          </div>

          <div className="service-mix-list">
            {serviceMix.map((service) => (
              <div key={service.name} className="service-mix-row">
                <div className="service-mix-meta">
                  <span className="service-mix-swatch tinted" style={tintStyle(service.color)} />
                  <strong>{service.name}</strong>
                  <span>{service.count} bookings</span>
                </div>
                <div className="service-mix-track">
                  <div
                    className="service-mix-fill tinted"
                    style={{ width: `${Math.max(8, service.share)}%`, ...tintStyle(service.color) }}
                  />
                </div>
                <span className="service-mix-share">{service.share}%</span>
              </div>
            ))}
            {serviceMix.length === 0 ? <div className="empty-state">No service mix data yet.</div> : null}
          </div>
        </div>
      </section>

      <section className="analysis-grid analysis-grid-secondary">
        <div className="panel">
          <div className="panel-title-row">
            <div>
              <h2 className="panel-title">Staff leaderboard</h2>
              <p className="panel-caption">Ranked by revenue</p>
            </div>
            <Link className="ghost-button" to="/staff">
              Directory
            </Link>
          </div>

          <div className="list">
            {staffRanking.map((staff, index) => (
              <Link className="list-item interactive" key={staff.id} to={`/staff/${staff.id}`}>
                <div className="media-row">
                  <div className="analysis-rank">{index + 1}</div>
                  <div className="avatar tinted" style={tintStyle(staff.color)}>
                    {getInitials(staff.name)}
                  </div>
                  <div>
                    <strong>{staff.name}</strong>
                    <span>{staff.role}</span>
                    <p>
                      {staff.bookings} bookings · {staff.retention}% retention · {staff.rating.toFixed(1)} rating
                    </p>
                  </div>
                </div>
                <div className="item-meta">
                  <strong>{formatInrCurrency(staff.revenue)}</strong>
                  <span className="badge soft">{staff.utilization}% util</span>
                </div>
              </Link>
            ))}
            {staffRanking.length === 0 ? <div className="empty-state">No staff performance data yet.</div> : null}
          </div>
        </div>

        <div className="stack">
          <div className="panel calendar-panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Booking density</h2>
                <p className="panel-caption">{homeData.month_label}</p>
              </div>
              <Link className="ghost-button" to="/calendar">
                Calendar
              </Link>
            </div>

            <div className="calendar-grid-head" aria-hidden="true">
              {WEEK_DAYS.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="calendar-grid analysis-calendar-grid" role="grid" aria-label="Booking density">
              {homeData.calendar.cells.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="calendar-cell empty" aria-hidden="true" />;
                }

                const density = homeData.calendar.booking_density[String(day)] ?? 0;
                const isHighlighted = homeData.calendar.highlighted_day === day;

                return (
                  <div key={day} className={`calendar-cell${isHighlighted ? ' highlighted' : ''}`}>
                    <span className="calendar-day">{day}</span>
                    {density > 0 ? (
                      <span className="calendar-density">{density}</span>
                    ) : (
                      <span className="calendar-density muted">•</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="calendar-summary">
              <div className="calendar-summary-item">
                <span>Peak day</span>
                <strong>{peakDay}</strong>
              </div>
              <div className="calendar-summary-item">
                <span>Busy days</span>
                <strong>{busyDayCount}</strong>
              </div>
              <div className="calendar-summary-item">
                <span>Top client</span>
                <strong>{topClient ? `${topClient.name} · ${topClient.total_visits}` : '—'}</strong>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Today</h2>
                <p className="panel-caption">{homeData.today_label}</p>
              </div>
            </div>

            <div className="list">
              {recentAppointments.length > 0 ? (
                recentAppointments.map((appointment) => (
                  <div className="list-item" key={appointment.id}>
                    <div>
                      <strong>{appointment.client_name}</strong>
                      <span>
                        {appointment.time} · {appointment.staff_name}
                      </span>
                      <p>{appointment.services.join(' · ')}</p>
                    </div>
                    <div className="item-meta">
                      <span className="badge soft">{appointment.status.replace('_', ' ')}</span>
                      <strong>{formatInrCurrency(appointment.total_amount)}</strong>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">No appointments for today.</div>
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Feedback</h2>
                <p className="panel-caption">
                  {overview?.feedback_count ?? feedbackItems.length} responses
                </p>
              </div>
            </div>

            <div className="list">
              {feedbackItems.length > 0 ? (
                feedbackItems.map((item) => (
                  <div className="list-item" key={item.id}>
                    <div>
                      <strong>{item.rating ? `${item.rating}/5` : 'Response'}</strong>
                      <p>{item.comment || item.feedback || 'No comment left.'}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">No feedback responses yet.</div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
